import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useParams } from 'react-router-dom';
import { useClinicDashboard } from '../context/ClinicDashboardContext';
import ViewHeader from '../components/ViewHeader';
import KpiCard from '../components/KpiCard';
import CategoryBar from '../components/CategoryBar';
import DateFilter from '../components/DateFilter';
import EmptyState from '../components/EmptyState';
import api from '../../../../services/api';

const STAY_COLORS = ['#0f766e', '#f97316', '#10b981', '#3b82f6'];

const CapacityUtilization = ({ onBack }) => {
    const { clinicId } = useParams();
    const { sharedMonthYear } = useClinicDashboard();
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ year: sharedMonthYear.year });
    const [data, setData] = useState({
        kpis: [],
        dailyAppointments: [],
        stayData: [],
        appointmentBars: [],
        capacityBars: [],
        resourceGrid: []
    });

    useEffect(() => {
        const fetchCapacityData = async () => {
            try {
                setLoading(true);
                const response = await api.get(`/api/v1/clinics/${clinicId}/dashboard/capacity?year=${filters.year}`);
                if (response.data?.status === 'success') {
                    setData(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching capacity data:", error);
            } finally {
                setLoading(false);
            }
        };

        if (clinicId) {
            fetchCapacityData();
        }
    }, [clinicId, filters]);

    if (loading) {
        return <div className="p-4">Loading capacity and utilization report...</div>;
    }

    const hasStayData = data.stayData.some(d => d.value > 0);
    const hasAppointmentBars = data.appointmentBars.some(b => b.value > 0);
    const hasCapacityBars = data.capacityBars.some(b => b.value > 0);
    const hasResourceGrid = data.resourceGrid.length > 0;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <ViewHeader
                title="Clinic Capacity and Utilization"
                subtitle={`Year ${filters.year}`}
                onBack={onBack}
                filter={
                    <DateFilter
                        mode="year"
                        value={filters}
                        onChange={setFilters}
                    />
                }
            />

            {/* Row 1: Charts on top */}
            <div className="clinic-dashboard-section-row">
                {/* Appointments by day */}
                <div className="clinic-dashboard-section-card" style={{ animationDelay: '0ms' }}>
                    <div className="clinic-dashboard-section-title">Appointments by day</div>
                    <div className="clinic-dashboard-chart-wrap" style={{ minHeight: '220px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data.dailyAppointments} barSize={24} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                    dy={10}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                />
                                <Tooltip
                                    cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                />
                                <Bar animationBegin={600} dataKey="value" fill="#0f766e" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Length of stay distribution */}
                <div className="clinic-dashboard-section-card" style={{ animationDelay: '80ms' }}>
                    <div className="clinic-dashboard-section-title">Length of stay distribution</div>
                    {hasStayData ? (
                        <div className="clinic-dashboard-chart-wrap" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '260px' }}>
                            <ResponsiveContainer width="100%" height="85%">
                                <PieChart>
                                    <Pie
                                        data={data.stayData}
                                        innerRadius={60}
                                        outerRadius={90}
                                        paddingAngle={3}
                                        dataKey="value"
                                        stroke="none"
                                    >
                                        {data.stayData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={STAY_COLORS[index % STAY_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="clinic-dashboard-legend">
                                {data.stayData.map((entry, index) => (
                                    <div key={index} className="clinic-dashboard-legend-item">
                                        <span className="clinic-dashboard-legend-dot" style={{ backgroundColor: STAY_COLORS[index] }} />
                                        <span className="clinic-dashboard-legend-label">{entry.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <EmptyState message="No stay data for this period" />
                    )}
                </div>
            </div>

            {/* Row 2: Appointments booked | Capacity utilization breakdown */}
            <div className="clinic-dashboard-section-row">
                {/* Appointments booked */}
                <div className="clinic-dashboard-section-card" style={{ alignSelf: 'start', animationDelay: '160ms' }}>
                    <div className="clinic-dashboard-section-title">Appointments booked</div>
                    {hasAppointmentBars ? (
                        <div className="clinic-dashboard-catbar-list">
                            {data.appointmentBars.map((bar, i) => <CategoryBar key={i} {...bar} />)}
                        </div>
                    ) : (
                        <EmptyState message="No appointment data for this period" />
                    )}
                </div>

                {/* Capacity utilization breakdown */}
                <div className="clinic-dashboard-section-card" style={{ alignSelf: 'start', animationDelay: '240ms' }}>
                    <div className="clinic-dashboard-section-title">Capacity utilization breakdown</div>
                    {hasCapacityBars ? (
                        <div className="clinic-dashboard-catbar-list">
                            {data.capacityBars.map((bar, i) => <CategoryBar key={i} {...bar} />)}
                        </div>
                    ) : (
                        <EmptyState message="No capacity data for this period" />
                    )}
                </div>
            </div>

            {/* Row 3: Resource availability | Current Utilization */}
            <div className="clinic-dashboard-section-row">
                {/* Resource availability */}
                <div className="clinic-dashboard-section-card" style={{ animationDelay: '320ms' }}>
                    <div className="clinic-dashboard-section-title">Resource availability</div>
                    {hasResourceGrid ? (
                        <div className="clinic-dashboard-resource-grid">
                            {data.resourceGrid.map((resource, i) => (
                                <div key={i} className="clinic-dashboard-resource-item">
                                    <div className="clinic-dashboard-resource-value">{resource.value}</div>
                                    <div className="clinic-dashboard-resource-label">{resource.label}</div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <EmptyState message="No resource data for this period" />
                    )}
                </div>

                {/* Current Utilization */}
                <div className="clinic-dashboard-section-card" style={{ animationDelay: '400ms' }}>
                    <div className="clinic-dashboard-section-title">Current Utilization</div>
                    <div className="clinic-dashboard-kpi-row">
                        {data.kpis.map((kpi, i) => <KpiCard key={i} {...kpi} />)}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CapacityUtilization;
